/**
 * Shipico pickup page, standalone theme copy for iNeonPlay.
 *
 * The same page as extensions/shipico-checkout as it was before 5d2b505 (with
 * name, phone and email), but with no app and no backend: the settings come
 * from the theme snippet, and the Econt and Speedy office lists from JSON
 * files in the theme's assets that fetch-offices.mjs writes. The theme's
 * checkout button opens a full-screen page where the shopper gives a name and
 * phone and picks an office or an address; the choice is written onto
 * Shopify's cart and Shopify's own checkout opens pre-filled. Nothing here
 * places an order or sends the shopper's details anywhere but Shopify.
 *
 * Design constraints this file lives under:
 *   - No dependencies. It ships into a theme where anything we add is weight
 *     on every page load.
 *   - No globals beyond window.ShipSolutions, which the snippet defines.
 *   - Fails silent-and-open: if the office lists cannot be read, the theme's
 *     own checkout button must still work. A broken integration that blocks
 *     checkout is worse than no integration.
 */
(function () {
  "use strict";

  var cfg = window.ShipSolutions;
  if (!cfg || !cfg.config || !cfg.offices) return;

  var config = null;
  // Trying the page before shoppers can see it: ?shipico_preview=1 on any
  // storefront URL, kept for the tab so the cart and product pages stay in
  // preview. previewOnly is set while the snippet's `enabled` is still false,
  // which is when the page says it is a preview.
  var preview = readPreview();
  var previewOnly = false;

  // --- storage -------------------------------------------------------------

  function readPreview() {
    var asked = /[?&]shipico_preview=1(&|$)/.test(window.location.search);
    try {
      if (asked) window.sessionStorage.setItem("ss_preview", "1");
      return window.sessionStorage.getItem("ss_preview") === "1";
    } catch (_e) {
      return asked;
    }
  }

  // --- offices from the theme ----------------------------------------------

  /** As many as the API answered with; the page draws the first 12. */
  var OFFICE_RESULTS = 40;

  /** One download per courier per page view; the theme's CDN caches the file. */
  var officeLists = {};
  function officeList(courier) {
    var url = cfg.offices[courier];
    if (!url) return Promise.reject(new Error("no office list for " + courier));
    if (!officeLists[courier]) {
      officeLists[courier] = fetch(url, { headers: { Accept: "application/json" } })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.json();
        })
        .then(function (data) {
          return data.offices || [];
        });
      // A failed download is tried again on the next search, not remembered.
      officeLists[courier].catch(function () {
        delete officeLists[courier];
      });
    }
    return officeLists[courier];
  }

  /**
   * How well an office matches what the shopper typed, or -1 for not at all.
   * Lower is better: the name or town starting with it, then a word of the
   * name, then anywhere in the name, then only the address. The API's
   * officeRank, so "пловдив" lists the offices in Plovdiv first.
   */
  function officeRank(q, office) {
    if (!q) return 0;
    var n = String(office.name || "").toLowerCase();
    if (
      n.indexOf(q) === 0 ||
      String(office.city || "")
        .toLowerCase()
        .indexOf(q) === 0
    )
      return 0;
    if (
      n.split(/[\s\-–]+/).some(function (word) {
        return word.indexOf(q) === 0;
      })
    )
      return 1;
    if (n.indexOf(q) !== -1) return 2;
    if (
      String(office.address || "")
        .toLowerCase()
        .indexOf(q) !== -1
    )
      return 3;
    return -1;
  }

  function bestOffices(offices, q) {
    return offices
      .map(function (office, index) {
        return { office: office, index: index, score: officeRank(q, office) };
      })
      .filter(function (entry) {
        return entry.score >= 0;
      })
      .sort(function (a, b) {
        return a.score - b.score || a.index - b.index;
      })
      .slice(0, OFFICE_RESULTS)
      .map(function (entry) {
        return entry.office;
      });
  }

  /** Great-circle distance in kilometres. */
  function distanceKm(a, b) {
    var rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad;
    var dLng = (b.lng - a.lng) * rad;
    var h =
      Math.pow(Math.sin(dLat / 2), 2) +
      Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.pow(Math.sin(dLng / 2), 2);
    return 6371 * 2 * Math.asin(Math.sqrt(h));
  }

  function nearestOffices(offices, point) {
    return offices
      .filter(function (o) {
        return typeof o.lat === "number" && typeof o.lng === "number";
      })
      .map(function (o) {
        var copy = {};
        for (var key in o) copy[key] = o[key];
        copy.distanceKm = Math.round(distanceKm(point, o) * 10) / 10;
        return copy;
      })
      .sort(function (a, b) {
        return a.distanceKm - b.distanceKm;
      })
      .slice(0, OFFICE_RESULTS);
  }

  /**
   * Stands in for the API the page was written against, so the rest of this
   * file is the extension's code unchanged: /api/checkout/config answers from
   * the snippet, /api/checkout/offices from the theme's office lists.
   */
  function request(path) {
    var query = {};
    var at = path.indexOf("?");
    (at === -1 ? "" : path.slice(at + 1)).split("&").forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf("=");
      var key = decodeURIComponent(eq === -1 ? pair : pair.slice(0, eq));
      query[key] = eq === -1 ? "" : decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, " "));
    });
    var route = at === -1 ? path : path.slice(0, at);

    if (route === "/api/checkout/config") {
      var enabled = cfg.config.enabled === true;
      if (!enabled && !preview) return Promise.reject(new Error("checkout is off"));
      return Promise.resolve({ config: cfg.config, preview: !enabled });
    }

    if (route === "/api/checkout/offices") {
      var courier = query.courier;
      if ((cfg.config.couriers || []).indexOf(courier) === -1) {
        return Promise.reject(new Error("courier not offered: " + courier));
      }
      return officeList(courier).then(function (offices) {
        var near = String(query.near || "").split(",");
        var point = { lat: Number(near[0]), lng: Number(near[1]) };
        if (near.length === 2 && isFinite(point.lat) && isFinite(point.lng) && near[0] && near[1]) {
          return { offices: nearestOffices(offices, point) };
        }
        return {
          offices: bestOffices(
            offices,
            String(query.q || "")
              .toLowerCase()
              .trim(),
          ),
        };
      });
    }

    return Promise.reject(new Error("unknown route " + route));
  }

  // --- DOM helpers ---------------------------------------------------------

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    for (var key in attrs || {}) {
      if (key === "class") node.className = attrs[key];
      else if (key === "text") node.textContent = attrs[key];
      else node.setAttribute(key, attrs[key]);
    }
    (children || []).forEach(function (child) {
      if (child) node.appendChild(child);
    });
    return node;
  }

  /** Field visibility per the merchant's settings; unknown fields are shown. */
  function mode(name) {
    return (config && config.fields && config.fields[name]) || "optional";
  }

  /**
   * What the pickup page's button says: the merchant's own wording from the
   * app settings, or the next step. The theme block's old "button text" was
   * written for the removed order form ("Поръчай с наложен платеж"), so it is
   * no longer read.
   */
  function submitLabel() {
    var configured =
      config && typeof config.buttonText === "string" ? config.buttonText.trim() : "";
    return configured || "Продължи към плащане";
  }

  /**
   * BOX NOW is a network of parcel machines and nothing else — no counters, no
   * courier at the door. Offering it for address delivery would be offering
   * something that cannot be bought.
   */
  var LOCKER_ONLY = { boxnow: true };

  // --- product tag rules ---------------------------------------------------

  /**
   * What the tagged products in the cart rule out: offices and lockers (a sofa
   * goes to an address), lockers alone (a bike fits no machine), or cash on
   * delivery (made to order). The merchant's tags, compared without regard to
   * case, the way Shopify compares them. Shopify's checkout applies the same
   * rules through our Functions; this is the page saying so first.
   */
  var NO_LIMITS = { addressOnly: false, noLocker: false, noCod: false };

  // Liquid knows every tag of the cart the page loaded with. Products added
  // later are looked up once each (cartLimits).
  var productTags = {};
  ((cfg.cart && cfg.cart.items) || []).forEach(function (item) {
    if (item.productId && item.tags) productTags[String(item.productId)] = item.tags;
  });

  function limitsFor(tagLists) {
    var rules = (config && config.tagRules) || [];
    if (!rules.length) return NO_LIMITS;
    var have = {};
    tagLists.forEach(function (tags) {
      (tags || []).forEach(function (tag) {
        have[String(tag).trim().toLowerCase()] = true;
      });
    });
    var limits = { addressOnly: false, noLocker: false, noCod: false };
    rules.forEach(function (r) {
      if (
        !have[
          String(r.tag || "")
            .trim()
            .toLowerCase()
        ]
      )
        return;
      if (r.rule === "address_only") limits.addressOnly = true;
      else if (r.rule === "no_locker") limits.noLocker = true;
      else if (r.rule === "no_cod") limits.noCod = true;
    });
    return limits;
  }

  /** For a cart read from /cart.js, which carries no tags. */
  function cartLimits(cart) {
    var items = (cart && cart.items) || [];
    if (!((config && config.tagRules) || []).length || !items.length) {
      return Promise.resolve(NO_LIMITS);
    }
    return Promise.all(
      items.map(function (item) {
        var id = String(item.product_id || "");
        if (productTags[id]) return productTags[id];
        if (!item.handle) return [];
        return fetch(storeRoot() + "/products/" + encodeURIComponent(item.handle) + ".js", {
          headers: { Accept: "application/json" },
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (product) {
            var tags = (product && product.tags) || [];
            if (typeof tags === "string") tags = tags.split(",");
            productTags[id] = tags;
            return tags;
          })
          .catch(function () {
            // Unknown is unrestricted here; Shopify's checkout still applies the rule.
            return [];
          });
      }),
    ).then(limitsFor);
  }

  /** The delivery types left once the products have had their say — never none. */
  function allowedTypes(types, limits) {
    if (!limits.addressOnly) return types;
    var left = types.filter(function (type) {
      return type !== "to_office";
    });
    return left.length ? left : types;
  }

  /** Offices without the lockers, when a product fits no locker. */
  function fitOffices(offices, limits) {
    if (!limits || !limits.noLocker) return offices;
    return offices.filter(function (office) {
      return office.kind !== "locker";
    });
  }

  /**
   * The rate for this cart, mirroring shopifyRateForCart on the server.
   *
   * Duplicated rather than shared because this file ships into a theme and can
   * import nothing. The server is the one that decides what is charged; this
   * only decides what is shown, and the two disagreeing is a bug either way —
   * hence the same rules, in the same order.
   */
  function shopifyRateForCart(profiles, productIds, deliveryType) {
    var keywords = (
      config && config.officeKeywords && config.officeKeywords.length
        ? config.officeKeywords
        : ["офис", "автомат", "pickup", "locker", "box"]
    ).map(function (word) {
      return String(word).trim().toLowerCase();
    });

    var inCart = {};
    productIds.forEach(function (id) {
      if (id) inCart[id] = true;
    });

    var named = profiles.filter(function (profile) {
      return (
        !profile.isDefault &&
        (profile.productIds || []).some(function (id) {
          return inCart[id];
        })
      );
    });
    var candidates = named.length
      ? named
      : profiles.filter(function (profile) {
          return profile.isDefault || profiles.length === 1;
        });

    var best = null;
    candidates.forEach(function (profile) {
      var matching = (profile.rates || []).filter(function (rate) {
        var haystack = String(rate.name).toLowerCase();
        var isOffice = keywords.some(function (word) {
          return word && haystack.indexOf(word) !== -1;
        });
        return deliveryType === "to_office" ? isOffice : !isOffice;
      });
      if (!matching.length) return;
      // Cheapest inside a profile, dearest across profiles — as on the server.
      var cheapest = matching.reduce(function (low, rate) {
        return Number(rate.price) < Number(low.price) ? rate : low;
      });
      if (!best || Number(cheapest.price) > Number(best.price)) best = cheapest;
    });
    return best;
  }

  /** The irrevocably fixed BGN/EUR rate. Mirrors BGN_PER_EUR on the server. */
  var BGN_PER_EUR = 1.95583;

  /**
   * A price with its counterpart currency beside it — "24.50 лв. / 12.53 €" —
   * as Bulgarian price-display rules require. Anything other than лева or евро
   * is shown on its own; there is no fixed rate to convert it against.
   */
  function money(value, cur) {
    var code = String(cur || "").toUpperCase();
    var primary = value.toFixed(2) + " " + cur;
    // Math.round on stotinki, not toFixed, so a half-cent lands the same way
    // here as in bgnToEur() on the server and the two totals never disagree.
    if (code === "BGN") return primary + " / " + convert(value, 1 / BGN_PER_EUR) + " EUR";
    if (code === "EUR") return primary + " / " + convert(value, BGN_PER_EUR) + " BGN";
    return primary;

    function convert(v, factor) {
      return (Math.round(Math.round(v * 100) * factor) / 100).toFixed(2);
    }
  }

  /**
   * The keys the choice travels under. Shopify copies cart attributes onto the
   * order as note_attributes, and our webhook reads them back off there.
   *
   * They are prefixed rather than pretty because they are a contract between
   * this script and a webhook arriving hours later — the same strings live in
   * packages/shared/src/cart-attributes.ts, and the two must not drift. The
   * office *name* is along purely so the merchant sees something meaningful in
   * Shopify's own admin, where a bare "4044" tells them nothing.
   */
  var ATTR = {
    delivery: "Shipico-delivery",
    courier: "Shipico-courier",
    office: "Shipico-office",
    officeName: "Shipico-office-name",
    officeKind: "Shipico-office-kind",
    invoiceCompany: "Shipico-invoice-company",
    invoiceEik: "Shipico-invoice-eik",
    invoiceVat: "Shipico-invoice-vat",
    invoiceMol: "Shipico-invoice-mol",
    invoiceAddress: "Shipico-invoice-address",
  };

  function storeRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return String(root).replace(/\/+$/, "");
  }

  function checkoutUrl() {
    return storeRoot() + "/checkout";
  }

  /**
   * Shopify's checkout with everything typed here already filled in.
   *
   * Built as a cart permalink (/cart/<variant>:<qty>,…), because that is where
   * Shopify reads checkout[email] and checkout[shipping_address][…], phone
   * included, and pre-fills them. The shopper can still change any of it
   * there. The attributes ride along, since a permalink starts a fresh cart.
   *
   * A permalink carries variants and quantities only, so a cart whose lines
   * have properties or a subscription plan goes to /checkout with the same
   * parameters instead: better an empty field than a lost engraving.
   */
  function prefilledCheckoutUrl(cart, prefill, attributes) {
    var items = (cart && cart.items) || [];
    var plain =
      items.length > 0 &&
      items.every(function (item) {
        var props = item.properties || {};
        var visible = Object.keys(props).filter(function (key) {
          return props[key] !== null && props[key] !== "";
        });
        return !visible.length && !item.selling_plan_allocation;
      });
    var base = plain
      ? storeRoot() +
        "/cart/" +
        items
          .map(function (item) {
            return item.variant_id + ":" + item.quantity;
          })
          .join(",")
      : checkoutUrl();

    var params = [];
    function add(key, value) {
      if (value) params.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    }
    add("checkout[email]", prefill.email);
    Object.keys(prefill.address).forEach(function (key) {
      add("checkout[shipping_address][" + key + "]", prefill.address[key]);
    });
    if (plain) {
      Object.keys(attributes).forEach(function (key) {
        add("attributes[" + key + "]", attributes[key]);
      });
      (cart.discount_codes || []).forEach(function (discount) {
        if (discount.applicable) add("discount", discount.code);
      });
    }
    return params.length ? base + "?" + params.join("&") : base;
  }

  // --- pickup page ---------------------------------------------------------

  /**
   * The pickup flow as a page of its own, opened over the shop.
   *
   * It used to sit inline under the cart, squeezed into whatever column the
   * theme gave its checkout button — on most themes a narrow box below the
   * fold with a scroll list inside it. Shoppers compare it with the COD forms
   * Bulgarian stores already use, and those are a full page: contact details
   * and delivery on the left, the order on the right, one column on a phone.
   *
   * Nothing is ordered here. The page collects what Shopify's checkout would
   * ask, saves the office on the cart, and opens that checkout with every
   * field filled in — the shopper only confirms.
   */
  // The logos come from the block (asset_url); the letter on the courier's
  // colour is what shows if a theme still runs an older block without them.
  var COURIER_BRANDS = {
    econt: { name: "Еконт", mark: "Е", color: "#234c9f", offices: "Офиси и автомати" },
    speedy: { name: "Speedy", mark: "S", color: "#ffffff", offices: "Офиси и автомати" },
    boxnow: {
      name: "BOX NOW",
      mark: "BN",
      color: "#44d62c",
      offices: "Автомати · плащане с карта",
    },
  };

  function courierMark(courier, brand) {
    var logo = cfg.logos && cfg.logos[courier];
    var mark = el("span", { class: "ss-mark ss-mark--" + courier });
    mark.style.background = brand.color;
    if (logo) mark.appendChild(el("img", { src: logo, alt: brand.name }));
    else mark.textContent = brand.mark;
    return mark;
  }

  var page = null;

  function pickupCouriers(type) {
    var all = (config && config.couriers) || ["econt"];
    var list =
      type === "to_office" && !page.limits.noLocker
        ? all
        : all.filter(function (c) {
            return !LOCKER_ONLY[c];
          });
    return list.length ? list : ["econt"];
  }

  /**
   * How much of the bottom of the window something above the page covers.
   *
   * Shopify's preview bar ("Password protected · Edit theme") sits along the
   * bottom for anyone signed in to the store's admin, in the browser's top
   * layer where no z-index reaches. App Review opens the storefront exactly
   * that way, and the page's button sat under the bar. Measured rather than
   * recognised by id, because the bar is Shopify's to change.
   *
   * Measured with the page at full height: whatever is hit then is above us.
   * Only a fixed strip counts, and only a modest one, so a theme's own
   * full-screen layer can never squash the page.
   */
  function bottomCovered() {
    var hit = document.elementFromPoint(Math.round(window.innerWidth / 2), window.innerHeight - 2);
    if (!hit || page.root.contains(hit)) return 0;
    for (var node = hit; node && node.nodeType === 1; node = node.parentElement) {
      if (window.getComputedStyle(node).position === "fixed") {
        var covered = window.innerHeight - node.getBoundingClientRect().top;
        return covered > 0 && covered <= 160 ? Math.ceil(covered) : 0;
      }
    }
    return 0;
  }

  function fitAboveBottomBar() {
    if (!page || page.root.hidden) return;
    page.root.style.bottom = "0px";
    page.root.style.bottom = bottomCovered() + "px";
  }

  function openPickupPage() {
    if (!page) {
      page = buildPickupPage();
      window.addEventListener("resize", fitAboveBottomBar);
    }
    if (!page.root.isConnected) document.body.appendChild(page.root);
    page.root.hidden = false;
    document.documentElement.classList.add("ss-page-open");
    fitAboveBottomBar();
    trackCart(
      refreshSummary().catch(function () {
        /* the summary is information only; the checkout still works */
      }),
    );
    warmOffices();
    var first = page.root.querySelector("input");
    if (first && first.focus) {
      try {
        first.focus({ preventScroll: true });
      } catch (_e) {
        first.focus();
      }
    }
  }

  /**
   * Ask for the office list once as the page opens, so the courier's list is
   * already cached on our side by the time the shopper types a town — the
   * first lookup is the slow one. The answer itself is thrown away.
   */
  var officesWarmed = false;
  function warmOffices() {
    if (officesWarmed || !page || page.deliveryType !== "to_office") return;
    officesWarmed = true;
    request("/api/checkout/offices?courier=" + encodeURIComponent(page.courier) + "&q=").catch(
      function () {
        /* a real search will try again */
      },
    );
  }

  function closePickupPage() {
    if (!page) return;
    page.root.hidden = true;
    document.documentElement.classList.remove("ss-page-open");
  }

  function input(id, label, type, attrs) {
    var node = el("input", { id: "ss-" + id, name: id, type: type || "text", placeholder: " " });
    for (var key in attrs || {}) node.setAttribute(key, attrs[key]);
    var wrap = el("label", { class: "ss-float", for: "ss-" + id }, [
      node,
      el("span", { text: label }),
    ]);
    return { wrap: wrap, input: node };
  }

  function buildPickupPage() {
    var p = {
      deliveryType: null,
      courier: null,
      office: null,
      cart: null,
      limits: NO_LIMITS,
      // Cart work in flight (see trackCart), whether the last read after an
      // add failed, and the newest cart read, so an older one landing late is
      // not drawn over it.
      cartWork: [],
      cartStale: false,
      refreshSeq: 0,
      submitting: false,
    };

    var close = el("button", { type: "button", class: "ss-page-close", "aria-label": "Затвори" });
    close.textContent = "×";
    close.addEventListener("click", closePickupPage);

    var header = el("div", { class: "ss-page-header" });
    var logoUrl = config.logoUrl ? String(config.logoUrl).trim() : "";
    if (/^https?:\/\//i.test(logoUrl)) {
      var logo = el("img", { class: "ss-page-logo", src: logoUrl, alt: "" });
      logo.style.height = Math.min(Number(config.logoHeight) || 32, 48) + "px";
      header.appendChild(logo);
    } else {
      header.appendChild(el("strong", { class: "ss-page-shop", text: shopName() }));
    }
    header.appendChild(close);

    // --- contact
    p.first = input("first-name", "Име", "text", { autocomplete: "given-name" });
    p.last = input("last-name", "Фамилия", "text", { autocomplete: "family-name" });
    p.email =
      mode("email") !== "hidden"
        ? input("email", "Имейл", "email", { autocomplete: "email" })
        : null;
    p.phone = input("phone", "Телефон", "tel", { autocomplete: "tel", inputmode: "tel" });
    p.phone.wrap.classList.add("ss-float--phone");
    // Drawn, not an emoji: Windows has no flag emoji and shows the letters "BG".
    var flag = el("span", { class: "ss-flag", "aria-hidden": "true" }, [
      el("i", { style: "background:#fff" }),
      el("i", { style: "background:#00966e" }),
      el("i", { style: "background:#d62612" }),
    ]);
    p.phone.wrap.appendChild(flag);

    var contact = el("div", { class: "ss-section" }, [
      el("h2", { class: "ss-h", text: "Вашите данни" }),
      el("div", { class: "ss-row" }, [p.first.wrap, p.last.wrap]),
    ]);
    if (p.email) contact.appendChild(p.email.wrap);
    contact.appendChild(p.phone.wrap);

    // --- delivery
    var types = (
      config.deliveryTypes && config.deliveryTypes.length ? config.deliveryTypes : ["to_office"]
    ).slice();
    p.types = types;
    p.deliveryType = types[0];
    p.tabs = el("div", { class: "ss-tabs", role: "tablist" });
    types.forEach(function (type) {
      var tab = el("button", {
        type: "button",
        class: "ss-tab",
        role: "tab",
        "data-type": type,
        text: type === "to_office" ? "До офис / автомат" : "До адрес",
      });
      tab.addEventListener("click", function () {
        p.deliveryType = type;
        p.office = null;
        renderDelivery();
      });
      p.tabs.appendChild(tab);
    });
    p.cards = el("div", { class: "ss-cards" });
    // Why a choice is missing, when a product in the cart took it away.
    p.limitNote = el("p", { class: "ss-limit-note" });
    p.limitNote.hidden = true;
    var delivery = el("div", { class: "ss-section" }, [
      el("h2", { class: "ss-h", text: "Избери начин за доставка" }),
    ]);
    if (types.length > 1) delivery.appendChild(p.tabs);
    delivery.appendChild(p.limitNote);
    delivery.appendChild(p.cards);

    p.error = el("div", { class: "ss-page-error", role: "alert" });
    p.error.hidden = true;
    p.submit = el("button", {
      type: "submit",
      class: "ss-page-submit",
      text: submitLabel() + " →",
    });
    var parts = [contact, delivery];
    var upsell = upsellSection(p);
    if (upsell) parts.push(upsell);
    var invoice = invoiceSection(p);
    if (invoice) parts.push(invoice);
    parts.push(p.error);
    var terms = termsCheckbox(p);
    if (terms) parts.push(terms);
    parts.push(p.submit);
    var form = el("form", { class: "ss-page-form", novalidate: "novalidate" }, parts);
    form.addEventListener("submit", onPickupPageSubmit);

    // --- order summary: aside on a desktop, a collapsible bar on a phone
    p.summaryToggle = el("button", { type: "button", class: "ss-summary-toggle" }, [
      el("span", { text: "Вашата поръчка" }),
      el("strong", { class: "ss-summary-total" }),
    ]);
    p.summaryToggle.addEventListener("click", function () {
      p.root.classList.toggle("ss-summary-open");
    });
    p.lines = el("div", { class: "ss-lines" });
    p.totals = el("div", { class: "ss-page-totals" });
    var aside = el("aside", { class: "ss-summary" }, [p.summaryToggle, p.lines, p.totals]);

    p.root = el(
      "div",
      { class: "ss-page", role: "dialog", "aria-modal": "true", "aria-label": "Поръчка" },
      [
        header,
        previewOnly ? previewNote() : null,
        el("div", { class: "ss-page-body" }, [
          el("main", { class: "ss-page-main" }, [form]),
          aside,
        ]),
      ],
    );
    p.root.hidden = true;
    p.root.addEventListener("keydown", function (event) {
      if (event.key === "Escape") closePickupPage();
    });
    applyPageAppearance(p.root);

    page = p;
    renderDelivery();
    return p;
  }

  /**
   * Says the page is a preview, so a merchant trying it does not wonder why
   * shoppers are not ordering through it yet — and does not mistake the
   * preview for having switched it on.
   */
  function previewNote() {
    return el("div", {
      class: "ss-preview-note",
      role: "status",
      text: "Преглед: купувачите още не виждат тази страница. Включва се с enabled: true в snippets/shipico-pickup.liquid.",
    });
  }

  /**
   * "Искам фактура", when the merchant offers it: a box that opens the company
   * fields. Closed by default — most shoppers are private people, and five
   * extra fields in front of every one of them would cost orders.
   */
  function invoiceSection(p) {
    if (!config.invoiceEnabled) return null;
    var want = el("input", { type: "checkbox", id: "ss-invoice" });
    p.invoice = {
      want: want,
      company: input("invoice-company", "Фирма", "text", { autocomplete: "organization" }),
      eik: input("invoice-eik", "ЕИК", "text", { inputmode: "numeric" }),
      vat: input("invoice-vat", "ДДС № (ако има)", "text"),
      mol: input("invoice-mol", "МОЛ", "text"),
      address: input("invoice-address", "Адрес на фирмата", "text"),
    };
    var fields = el("div", { class: "ss-invoice-fields" }, [
      p.invoice.company.wrap,
      el("div", { class: "ss-row" }, [p.invoice.eik.wrap, p.invoice.vat.wrap]),
      p.invoice.mol.wrap,
      p.invoice.address.wrap,
    ]);
    fields.hidden = true;
    want.addEventListener("change", function () {
      fields.hidden = !want.checked;
    });
    return el("div", { class: "ss-section ss-invoice" }, [
      el("label", { class: "ss-check", for: "ss-invoice" }, [
        want,
        el("span", { text: "Искам фактура" }),
      ]),
      fields,
    ]);
  }

  /** The shop's terms, to be accepted before continuing, when it has given a link. */
  function termsCheckbox(p) {
    var url = String(config.termsUrl || "");
    if (!/^https?:\/\//i.test(url)) return null;
    p.terms = el("input", { type: "checkbox", id: "ss-terms" });
    return el("label", { class: "ss-check ss-terms", for: "ss-terms" }, [
      p.terms,
      el("span", {}, [
        document.createTextNode("Прочетох и приемам "),
        el("a", { href: url, target: "_blank", rel: "noopener", text: "общите условия" }),
      ]),
    ]);
  }

  /**
   * "Може да Ви хареса": up to three products to add before checkout, when the
   * merchant switched it on. Either Shopify's own complementary products for
   * what is in the cart (set in the free Search & Discovery app) or the
   * products the merchant picked.
   *
   * Everything shown — title, picture, price, stock — is read live from the
   * storefront, never from our settings, so a price change or a sell-out never
   * shows a stale offer. Products already in the cart are not offered.
   */
  var MAX_UPSELLS = 3;

  function upsellSection(p) {
    var upsell = config.upsell;
    if (!upsell || !upsell.enabled) return null;
    p.upsell = {
      list: el("div", { class: "ss-upsell-list" }),
      error: el("p", { class: "ss-upsell-error", role: "alert" }),
      candidates: null,
      loading: null,
      /** Product ids added from here, shown as added rather than dropped from the list. */
      added: {},
    };
    p.upsell.error.hidden = true;
    p.upsell.section = el("div", { class: "ss-section ss-upsell" }, [
      el("h2", { class: "ss-h", text: upsell.heading || "Може да Ви хареса" }),
      p.upsell.list,
      p.upsell.error,
    ]);
    p.upsell.section.hidden = true;
    return p.upsell.section;
  }

  function fetchJson(url) {
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  /** The products to offer, fetched once per page. */
  function upsellCandidates(cart) {
    var u = page.upsell;
    if (u.candidates) return Promise.resolve(u.candidates);
    if (u.loading) return u.loading;
    var root = storeRoot();
    var upsell = config.upsell;
    var load;
    if (upsell.source === "products") {
      load = Promise.all(
        (upsell.products || []).slice(0, MAX_UPSELLS).map(function (product) {
          return fetchJson(root + "/products/" + encodeURIComponent(product.handle) + ".js");
        }),
      );
    } else {
      var first = (cart.items || [])[0];
      if (!first) return Promise.resolve([]);
      var base = root + "/recommendations/products.json?limit=6&product_id=" + first.product_id;
      load = fetchJson(base + "&intent=complementary").then(function (res) {
        var products = (res && res.products) || [];
        // No complementary products set up: Shopify's related ones still fit.
        if (products.length) return products;
        return fetchJson(base + "&intent=related").then(function (related) {
          return (related && related.products) || [];
        });
      });
    }
    u.loading = load.then(function (products) {
      u.candidates = (products || []).filter(function (product) {
        return product && product.id && product.available && (product.variants || []).length;
      });
      return u.candidates;
    });
    return u.loading;
  }

  function renderUpsells(cart) {
    var u = page.upsell;
    if (!u) return;
    upsellCandidates(cart).then(function (candidates) {
      if (page.cart !== cart) return;
      var inCart = {};
      (cart.items || []).forEach(function (item) {
        inCart[item.product_id] = true;
      });
      var offers = candidates
        .filter(function (product) {
          return u.added[product.id] || !inCart[product.id];
        })
        .slice(0, MAX_UPSELLS);
      u.list.textContent = "";
      offers.forEach(function (product) {
        u.list.appendChild(upsellCard(product, cart.currency));
      });
      u.section.hidden = offers.length === 0;
    });
  }

  function upsellImage(product) {
    var src = product.featured_image;
    if (src && typeof src === "object") src = src.url || src.src;
    if (!src) return el("span", { class: "ss-upsell-img" });
    src = String(src).replace(/^\/\//, "https://");
    return el("img", {
      class: "ss-upsell-img",
      src: src.replace(/(\.[a-z]+)(\?|$)/i, "_160x$1$2"),
      alt: "",
      loading: "lazy",
    });
  }

  function upsellCard(product, currency) {
    var u = page.upsell;
    var variants = product.variants.filter(function (variant) {
      return variant.available;
    });
    var picker = null;
    if (variants.length > 1) {
      picker = el(
        "select",
        { class: "ss-upsell-variant", "aria-label": "Вариант на " + product.title },
        variants.map(function (variant) {
          return el("option", { value: String(variant.id), text: variant.title });
        }),
      );
    }
    var price = el("span", { class: "ss-upsell-price" });
    function showPrice() {
      var variant = picker
        ? variants.filter(function (v) {
            return String(v.id) === picker.value;
          })[0]
        : variants[0];
      price.textContent = money((variant || variants[0]).price / 100, currency);
    }
    showPrice();
    if (picker) picker.addEventListener("change", showPrice);

    var added = !!u.added[product.id];
    var button = el("button", {
      type: "button",
      class: "ss-upsell-add",
      text: added ? "Добавено ✓" : "Добави",
    });
    // Drawn locked during a submit too: the summary is redrawn while it waits.
    button.disabled = added || page.submitting;
    if (picker) picker.disabled = added || page.submitting;
    button.addEventListener("click", function () {
      // Never during a submit, however the click got here. Once the choice is
      // being written to the cart, the checkout link is built from what that
      // write returns, and an add landing after it would be left out.
      if (page.submitting) return;
      var variantId = picker ? picker.value : String(variants[0].id);
      button.disabled = true;
      button.textContent = "Добавям…";
      u.error.hidden = true;
      // No line properties: a cart line with properties cannot ride in the
      // pre-filled checkout link, and the shopper would lose what they typed.
      var adding = fetch(cartPath() + "/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      }).then(
        function (res) {
          if (!res.ok) throw cartError("add");
          u.added[product.id] = true;
          // Lines, totals, and the delivery choices a tag on it may narrow.
          return refreshSummary().catch(function () {
            // In the cart, but the page could not read it back: the next
            // submit reads it again before going anywhere.
            page.cartStale = true;
            button.textContent = "Добавено ✓";
            throw cartError("refresh");
          });
        },
        function () {
          throw cartError("add");
        },
      );
      trackCart(adding).catch(function (error) {
        if (error.kind !== "add") return;
        // Still locked if a submit is waiting on this add; it unlocks the
        // offers itself when it gives up.
        button.disabled = page.submitting;
        button.textContent = "Добави";
        if (picker) picker.disabled = page.submitting;
        u.error.textContent = "Продуктът не можа да се добави. Опитайте отново.";
        u.error.hidden = false;
      });
    });

    return el(
      "div",
      {
        class: "ss-upsell-card" + (added ? " ss-upsell-card--added" : ""),
        "data-product": String(product.id),
      },
      [
        upsellImage(product),
        el(
          "div",
          { class: "ss-upsell-info" },
          [el("strong", { class: "ss-upsell-title", text: product.title }), price, picker].filter(
            Boolean,
          ),
        ),
        button,
      ],
    );
  }

  /** What the shopper typed for the invoice, or null when they want none. */
  function invoiceDetails() {
    if (!page.invoice || !page.invoice.want.checked) return null;
    function value(field) {
      return String(field.input.value || "").trim();
    }
    return {
      company: value(page.invoice.company),
      eik: value(page.invoice.eik).replace(/\s+/g, ""),
      vat: value(page.invoice.vat).replace(/\s+/g, "").toUpperCase(),
      mol: value(page.invoice.mol),
      address: value(page.invoice.address),
    };
  }

  /** The shop's own name, as on its storefront; the domain only as a last resort. */
  function shopName() {
    if (cfg.shopName) return String(cfg.shopName);
    var host = String(cfg.shop || "").replace(/\.myshopify\.com$/, "");
    return host ? host.charAt(0).toUpperCase() + host.slice(1) : "";
  }

  function applyPageAppearance(root) {
    root.style.setProperty("--ss-accent", config.accentColor || "#111827");
    root.style.setProperty("--ss-accent-text", config.buttonTextColor || "#ffffff");
    if (config.styleSource === "custom") {
      root.style.setProperty("--ss-radius", (config.cornerRadius || 0) + "px");
      root.style.setProperty("--ss-transform", config.uppercase ? "uppercase" : "none");
    }
  }

  /** Tabs and courier cards for the delivery type chosen. */
  function renderDelivery() {
    // The cards are rebuilt below, taking the map's container with them.
    destroyMap();
    var limits = page.limits;
    var types = allowedTypes(page.types, limits);
    if (types.indexOf(page.deliveryType) === -1) {
      page.deliveryType = types[0];
      page.office = null;
    }
    // Picked before the cart's tags were known.
    if (limits.noLocker && page.office && page.office.kind === "locker") page.office = null;
    Array.prototype.forEach.call(page.tabs.children, function (tab) {
      var type = tab.getAttribute("data-type");
      var on = type === page.deliveryType;
      tab.hidden = types.indexOf(type) === -1;
      tab.classList.toggle("ss-tab--on", on);
      tab.setAttribute("aria-selected", String(on));
    });
    page.tabs.hidden = types.length < 2;
    var note =
      limits.addressOnly && types.indexOf("to_office") === -1
        ? "Някои продукти в поръчката се доставят само до адрес."
        : limits.noLocker && page.deliveryType === "to_office"
          ? "Някои продукти не се побират в автомат, затова показваме само офиси."
          : "";
    page.limitNote.textContent = note;
    page.limitNote.hidden = !note;
    var couriers = pickupCouriers(page.deliveryType);
    if (couriers.indexOf(page.courier) === -1) page.courier = couriers[0];
    // An office and the last list of offices belong to the courier they came
    // from. However the courier changed — a click, or a tag rule taking BOX
    // NOW away — another courier's office id must never be sent as this one's.
    if (page.office && page.officeCourier !== page.courier) page.office = null;
    if (page.lastOffices && page.lastOffices.courier !== page.courier) page.lastOffices = null;

    page.cards.textContent = "";
    couriers.forEach(function (courier) {
      var brand = COURIER_BRANDS[courier] || { name: courier, mark: "?", color: "#555" };
      var on = courier === page.courier;
      var price = pickupPrice();
      var badge = config.courierBadges && config.courierBadges[courier];
      var head = el("button", { type: "button", class: "ss-card-head" }, [
        courierMark(courier, brand),
        el("span", { class: "ss-card-name" }, [
          el("strong", {
            text:
              page.deliveryType !== "to_office"
                ? "До адрес с " + brand.name
                : LOCKER_ONLY[courier]
                  ? "До автомат на " + brand.name
                  : limits.noLocker
                    ? "До офис на " + brand.name
                    : "До офис или автомат на " + brand.name,
          }),
          el("span", {
            text:
              page.deliveryType !== "to_office"
                ? "Доставка до врата"
                : limits.noLocker
                  ? "Само офиси"
                  : brand.offices,
          }),
        ]),
        el("span", { class: "ss-card-price", text: price }),
      ]);
      // The merchant's own word for this courier, above its name.
      if (badge) {
        var name = head.querySelector(".ss-card-name");
        name.insertBefore(el("em", { class: "ss-badge", text: String(badge) }), name.firstChild);
      }
      var card = el(
        "div",
        { class: "ss-card" + (on ? " ss-card--on" : ""), "data-courier": courier },
        [head],
      );
      head.addEventListener("click", function () {
        if (page.courier === courier) return;
        page.courier = courier;
        page.office = null;
        renderDelivery();
      });
      if (on)
        card.appendChild(
          page.deliveryType === "to_office" ? officePicker(courier) : addressFields(),
        );
      page.cards.appendChild(card);
    });
    refreshTotals();
  }

  /** "Безплатно", a price, or nothing when only Shopify's checkout knows. */
  function pickupPrice() {
    var profiles = config.shopifyProfiles;
    if (!profiles || !profiles.length || !page.cart) return "";
    var ids = (page.cart.items || []).map(function (item) {
      return item.product_id ? String(item.product_id) : null;
    });
    var rate = shopifyRateForCart(profiles, ids, page.deliveryType);
    if (!rate) return "";
    var threshold = config.freeShippingThreshold;
    var net = (page.cart.total_price || 0) / 100;
    if (threshold !== null && threshold !== undefined && net >= Number(threshold))
      return "Безплатно";
    return Number(rate.price) === 0
      ? "Безплатно"
      : money(Number(rate.price), page.cart.currency || cfg.currency || "EUR");
  }

  function officePicker(courier) {
    var box = el("div", { class: "ss-card-body" });
    if (page.office) {
      var change = el("button", { type: "button", class: "ss-link", text: "Промени" });
      change.addEventListener("click", function () {
        page.office = null;
        renderDelivery();
      });
      var picked = el("div", {}, [
        el("strong", { text: page.office.name }),
        el("span", { text: page.office.address || "" }),
      ]);
      if (officeHours(page.office))
        picked.appendChild(el("small", { text: officeHours(page.office) }));
      box.appendChild(el("div", { class: "ss-picked" }, [picked, change]));
      return box;
    }
    var search = el("input", {
      type: "search",
      class: "ss-search",
      id: "ss-office-search",
      placeholder: "Напиши град, офис или адрес",
      autocomplete: "off",
    });
    var results = el("div", { class: "ss-results", role: "listbox" });
    var timer = null;
    search.addEventListener("input", function () {
      clearTimeout(timer);
      var q = search.value.trim();
      if (q.length < 2) {
        results.textContent = "";
        return;
      }
      // Said at once: the first lookup of a courier can take seconds while
      // its office list is fetched, and an empty box reads as broken.
      results.textContent = "";
      results.appendChild(el("p", { class: "ss-muted", role: "status", text: "Търсене…" }));
      // Debounced: the lookup is rate-limited and runs on the merchant's
      // courier account, not a free service.
      timer = setTimeout(function () {
        searchOffices(courier, q, results);
      }, 250);
    });
    var mapHolder = el("div", { class: "ss-map" });
    mapHolder.hidden = true;
    var nearest = el("button", { type: "button", class: "ss-tool", text: "📍 Най-близо до мен" });
    nearest.addEventListener("click", function () {
      search.value = "";
      findNearest(courier, results);
    });
    var mapToggle = el("button", { type: "button", class: "ss-tool", text: "🗺 Карта" });
    mapToggle.addEventListener("click", function () {
      if (!mapHolder.hidden) {
        destroyMap();
        mapHolder.hidden = true;
        mapToggle.textContent = "🗺 Карта";
        return;
      }
      mapToggle.textContent = "Скрий картата";
      showMap(courier, mapHolder);
    });
    var tools = el("div", { class: "ss-tools" }, [nearest]);
    // The map needs a browser that can draw WebGL; without it the list is all.
    if (canDrawMap()) tools.appendChild(mapToggle);
    box.appendChild(el("div", { class: "ss-search-wrap" }, [search]));
    box.appendChild(tools);
    box.appendChild(mapHolder);
    box.appendChild(results);
    return box;
  }

  var officeSearchSeq = 0;

  function officesPath(courier) {
    return "/api/checkout/offices?courier=" + encodeURIComponent(courier);
  }

  function searchOffices(courier, q, results) {
    // Only the newest answer is drawn: a slow first lookup must not land over
    // the results of what the shopper typed after it.
    var seq = ++officeSearchSeq;
    request(officesPath(courier) + "&q=" + encodeURIComponent(q))
      .then(function (data) {
        if (page.courier !== courier || seq !== officeSearchSeq) return;
        showOffices(courier, fitOffices(data.offices || [], page.limits).slice(0, 12), results);
      })
      .catch(function () {
        if (seq !== officeSearchSeq) return;
        officeStatus(results, "Офисите не могат да се заредят. Опитайте отново.");
      });
  }

  function officeStatus(results, text) {
    results.textContent = "";
    results.appendChild(el("p", { role: "status", class: "ss-muted", text: text }));
  }

  /** "Пн–Пт 09:00–18:00 · Сб 09:00–13:00", "Денонощно", or nothing when unknown. */
  function officeHours(office) {
    var h = office && office.hours;
    if (!h) return "";
    if (h === "always") return "Денонощно";
    return "Пн–Пт " + h.weekdays + (h.saturday ? " · Сб " + h.saturday : " · Сб почивен ден");
  }

  function officeDistance(office) {
    var d = office.distanceKm;
    if (typeof d !== "number") return "";
    return d < 1 ? Math.round(d * 1000) + " м" : String(d).replace(".", ",") + " км";
  }

  /** Take an office as the destination — only one of the courier on screen. */
  function pickOffice(office, courier) {
    if (courier !== page.courier) return;
    page.office = office;
    page.officeCourier = courier;
    page.error.hidden = true;
    renderDelivery();
  }

  /** The list under the search box, and the same offices on the map when it is open. */
  function showOffices(courier, offices, results) {
    offices = fitOffices(offices, page.limits);
    page.lastOffices = { courier: courier, offices: offices };
    drawOnMap(courier, offices, true);
    results.textContent = "";
    if (!offices.length) {
      results.appendChild(el("p", { class: "ss-muted", text: "Няма намерени офиси." }));
      return;
    }
    offices.forEach(function (office) {
      var locker = office.kind === "locker";
      var item = el("button", { type: "button", class: "ss-result", role: "option" }, [
        el("strong", { text: office.name }),
        el("span", { text: office.address || "" }),
      ]);
      var meta = [officeHours(office), officeDistance(office)].filter(Boolean).join(" · ");
      if (meta) item.appendChild(el("small", { class: "ss-result-meta", text: meta }));
      if (office.kind) {
        // A locker takes a card only, even for cash on delivery. Said here,
        // not discovered in front of the machine.
        item.appendChild(el("em", { text: locker ? "автомат · плащане с карта" : "офис" }));
      }
      item.addEventListener("click", function () {
        pickOffice(office, courier);
      });
      results.appendChild(item);
    });
  }

  /**
   * The offices closest to the shopper. The position is rounded to about a
   * kilometre before it leaves the browser: enough to rank offices, too coarse
   * to be an address sitting in our request logs.
   */
  function findNearest(courier, results) {
    var seq = ++officeSearchSeq;
    if (!window.navigator.geolocation) {
      officeStatus(results, "Браузърът не споделя местоположение. Потърсете по град.");
      return;
    }
    officeStatus(results, "Определяме къде сте…");
    window.navigator.geolocation.getCurrentPosition(
      function (pos) {
        if (seq !== officeSearchSeq) return;
        var near = pos.coords.latitude.toFixed(2) + "," + pos.coords.longitude.toFixed(2);
        request(officesPath(courier) + "&near=" + near)
          .then(function (data) {
            if (page.courier !== courier || seq !== officeSearchSeq) return;
            showOffices(courier, fitOffices(data.offices || [], page.limits).slice(0, 12), results);
          })
          .catch(function () {
            if (seq !== officeSearchSeq) return;
            officeStatus(results, "Офисите не могат да се заредят. Опитайте отново.");
          });
      },
      function () {
        if (seq !== officeSearchSeq) return;
        officeStatus(results, "Нямаме достъп до местоположението ви. Потърсете по град.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }

  // --- map -------------------------------------------------------------------

  // MapLibre draws the map and OpenFreeMap serves the tiles: both free for
  // commercial use and keyless. Loaded only when a shopper opens the map, so
  // the storefront pays nothing for it otherwise. The version is pinned: an
  // update must never reach merchants' storefronts unreviewed.
  var MAPLIBRE = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl";
  // iNeonPlay copy: the dark style, to sit on the store's near-black page.
  var MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
  var BULGARIA = { center: [25.3, 42.7], zoom: 6 };
  // MapLibre's own words, which it would otherwise show in English.
  var MAP_LOCALE = {
    "CooperativeGesturesHandler.WindowsHelpText": "Ctrl + колелцето приближава картата",
    "CooperativeGesturesHandler.MacHelpText": "⌘ + колелцето приближава картата",
    "CooperativeGesturesHandler.MobileHelpText": "Местете картата с два пръста",
    "NavigationControl.ZoomIn": "Приближи",
    "NavigationControl.ZoomOut": "Отдалечи",
    "AttributionControl.ToggleAttribution": "Източник на картата",
    "Popup.Close": "Затвори",
    "Marker.Title": "Офис на картата",
  };
  var mapLibrary = null;

  function canDrawMap() {
    try {
      var canvas = document.createElement("canvas");
      return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"));
    } catch (_e) {
      return false;
    }
  }

  function loadMapLibrary() {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (mapLibrary) return mapLibrary;
    mapLibrary = new Promise(function (resolve, reject) {
      var css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = MAPLIBRE + ".css";
      document.head.appendChild(css);
      var script = document.createElement("script");
      script.src = MAPLIBRE + ".js";
      script.async = true;
      script.onload = function () {
        if (window.maplibregl) resolve(window.maplibregl);
        else reject(new Error("maplibre missing"));
      };
      script.onerror = function () {
        mapLibrary = null; // the next click may try again
        reject(new Error("maplibre failed"));
      };
      document.head.appendChild(script);
    });
    return mapLibrary;
  }

  function destroyMap() {
    if (!page || !page.map) return;
    try {
      page.map.map.remove();
    } catch (_e) {
      /* already gone with its container */
    }
    page.map = null;
  }

  function showMap(courier, holder) {
    holder.hidden = false;
    officeStatus(holder, "Картата се зарежда…");
    loadMapLibrary()
      .then(function (lib) {
        if (!holder.isConnected || page.courier !== courier) return;
        holder.textContent = "";
        var map = new lib.Map({
          container: holder,
          style: MAP_STYLE,
          center: BULGARIA.center,
          zoom: BULGARIA.zoom,
          attributionControl: { compact: true },
          cooperativeGestures: true,
          locale: MAP_LOCALE,
        });
        map.addControl(new lib.NavigationControl({ showCompass: false }), "top-right");
        page.map = { map: map, markers: [], courier: courier };
        // What the list shows, if it is this courier's; renderDelivery drops another's.
        var shown = page.lastOffices;
        if (shown && shown.courier === courier && shown.offices.length) {
          drawOnMap(courier, shown.offices, true);
        }

        // Zoomed in far enough to mean a neighbourhood, the map shows what is
        // there: the offices nearest its centre, reloaded as it is moved.
        // Debounced — the lookup is rate-limited per shopper.
        var timer = null;
        var seq = 0;
        map.on("moveend", function () {
          clearTimeout(timer);
          if (map.getZoom() < 11) return;
          timer = setTimeout(function () {
            var c = map.getCenter();
            var mine = ++seq;
            request(officesPath(courier) + "&near=" + c.lat.toFixed(4) + "," + c.lng.toFixed(4))
              .then(function (data) {
                if (mine !== seq || !page.map || page.map.map !== map) return;
                drawOnMap(courier, data.offices || [], false);
              })
              .catch(function () {
                /* the markers already drawn stay */
              });
          }, 400);
        });
      })
      .catch(function () {
        officeStatus(holder, "Картата не може да се зареди. Потърсете по град по-горе.");
      });
  }

  /** One courier's offices as markers, on a map opened for that courier only. */
  function drawOnMap(courier, offices, fit) {
    var m = page.map;
    var lib = window.maplibregl;
    if (!m || !lib || m.courier !== courier) return;
    m.markers.forEach(function (marker) {
      marker.remove();
    });
    m.markers = [];
    var bounds = new lib.LngLatBounds();
    fitOffices(offices, page.limits).forEach(function (office) {
      if (typeof office.lat !== "number" || typeof office.lng !== "number") return;
      var pick = el("button", { type: "button", class: "ss-map-pick", text: "Избери" });
      pick.addEventListener("click", function () {
        pickOffice(office, courier);
      });
      var popup = el("div", { class: "ss-map-popup" }, [
        el("strong", { text: office.name }),
        el("span", { text: office.address || "" }),
      ]);
      if (officeHours(office)) popup.appendChild(el("small", { text: officeHours(office) }));
      popup.appendChild(pick);
      var marker = new lib.Marker({
        // Lockers in the store's violet: grey disappears on the dark map.
        color: office.kind === "locker" ? "#8f86ff" : config.accentColor || "#111827",
      })
        .setLngLat([office.lng, office.lat])
        .setPopup(new lib.Popup({ offset: 24, closeButton: false }).setDOMContent(popup))
        .addTo(m.map);
      m.markers.push(marker);
      bounds.extend([office.lng, office.lat]);
    });
    if (fit && m.markers.length) {
      m.map.fitBounds(bounds, { padding: 40, maxZoom: 14, duration: 0 });
    }
  }

  function addressFields() {
    if (!page.address) {
      page.address = {
        city: input("city", "Град", "text", { autocomplete: "address-level2" }),
        street: input("address", "Адрес (улица, номер, вход, етаж)", "text", {
          autocomplete: "street-address",
        }),
        zip: input("zip", "Пощенски код", "text", {
          autocomplete: "postal-code",
          inputmode: "numeric",
        }),
      };
    }
    return el("div", { class: "ss-card-body" }, [
      el("div", { class: "ss-row" }, [page.address.city.wrap, page.address.zip.wrap]),
      page.address.street.wrap,
    ]);
  }

  /**
   * Items, subtotal and total, read fresh from Shopify's cart, and the
   * delivery choices its products' tags allow. Resolves once both are on the
   * page; rejects when the cart could not be read. Only the newest read is
   * drawn: two add-ons added together each read the cart, and the first
   * answer can arrive last.
   */
  function refreshSummary() {
    var seq = ++page.refreshSeq;
    return fetch(cartPath() + ".js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        if (!res.ok) throw new Error("cart read failed with " + res.status);
        return res.json();
      })
      .then(function (cart) {
        if (seq !== page.refreshSeq) return null;
        page.cart = cart;
        page.lines.textContent = "";
        (cart.items || []).forEach(function (item) {
          var image = item.image
            ? el("img", {
                src: String(item.image).replace(/(\.[a-z]+)(\?|$)/i, "_120x$1$2"),
                alt: "",
              })
            : el("span");
          page.lines.appendChild(
            el("div", { class: "ss-line" }, [
              el("span", { class: "ss-thumb" }, [image, el("b", { text: String(item.quantity) })]),
              el("span", { class: "ss-line-title" }, [
                el("strong", { text: item.product_title || item.title }),
                el("span", { text: item.variant_title || "" }),
              ]),
              el("span", {
                class: "ss-line-price",
                text: money(item.final_line_price / 100, cart.currency),
              }),
            ]),
          );
        });
        renderDelivery();
        renderUpsells(cart);
        return cartLimits(cart).then(function (limits) {
          if (seq !== page.refreshSeq) return;
          page.limits = limits;
          renderDelivery();
        });
      });
  }

  /**
   * Cart work started from the page — an add-on being added, and the cart
   * read and tag limits after it. A submit waits for all of it: Shopify's
   * checkout link is built from the cart as it stands, and one built while an
   * add is still on its way leaves the add-on out — the shopper sees it in
   * the summary and finds it missing at payment.
   */
  function trackCart(promise) {
    var entry = promise.then(
      function () {
        return null;
      },
      function (error) {
        return (error && error.kind) || "refresh";
      },
    );
    page.cartWork.push(entry);
    entry.then(function () {
      var i = page.cartWork.indexOf(entry);
      if (i !== -1) page.cartWork.splice(i, 1);
    });
    return promise;
  }

  /** Resolves once no cart work is in flight, with the first failure seen ("add" or "refresh"), or null. */
  function cartSettled() {
    if (!page.cartWork.length) return Promise.resolve(null);
    return Promise.all(page.cartWork.slice()).then(function (failures) {
      var failed =
        failures.filter(function (kind) {
          return kind;
        })[0] || null;
      // Work started while waiting — a second add-on — is waited for too.
      return cartSettled().then(function (later) {
        return failed || later;
      });
    });
  }

  function cartError(kind) {
    var error = new Error("cart " + kind + " failed");
    error.kind = kind;
    return error;
  }

  function refreshTotals() {
    if (!page.cart) return;
    var cur = page.cart.currency || cfg.currency || "EUR";
    var sub = page.cart.total_price / 100;
    var price = pickupPrice();
    page.totals.textContent = "";
    page.totals.appendChild(totalRow("Междинна сума", money(sub, cur)));
    page.totals.appendChild(totalRow("Доставка", price || "Изчислява се в следващата стъпка"));
    page.totals.appendChild(totalRow("Общо", money(sub, cur), true));
    page.summaryToggle.querySelector(".ss-summary-total").textContent = money(sub, cur);

    function totalRow(label, value, strong) {
      return el("div", { class: "ss-page-total" + (strong ? " ss-page-total--strong" : "") }, [
        el("span", { text: label }),
        el("span", { text: value }),
      ]);
    }
  }

  function pageError(message) {
    page.error.textContent = message;
    page.error.hidden = false;
    if (page.error.scrollIntoView)
      page.error.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  /** What to pre-fill in Shopify's checkout, from what was typed here. */
  function pickupPrefill() {
    var address = {
      first_name: page.first.input.value.trim(),
      last_name: page.last.input.value.trim(),
      phone: page.phone.input.value.trim(),
      country: "Bulgaria",
    };
    if (page.deliveryType === "to_office") {
      // The office is the shipping address, so the shopper types none.
      var office = page.office || {};
      var brand = (COURIER_BRANDS[page.courier] || {}).name || "";
      var name = office.name || "";
      var branded = brand && name.toLowerCase().indexOf(brand.toLowerCase()) === 0;
      address.address1 = office.address || name;
      address.address2 = branded ? name : [brand, name].filter(Boolean).join(" ");
      address.city = office.city || "";
      address.zip = office.postCode || "";
    } else {
      address.address1 = page.address.street.input.value.trim();
      address.city = page.address.city.input.value.trim();
      address.zip = page.address.zip.input.value.trim();
    }
    return { email: (page.email && page.email.input.value.trim()) || "", address: address };
  }

  /**
   * Save the choice on the cart, then open Shopify's checkout pre-filled.
   *
   * The order of those two is the whole thing. Navigating first would race the
   * write, and a checkout that starts before the attribute lands produces an
   * order with no office on it — which looks exactly like an order that never
   * went through the picker, and gets labelled by guesswork.
   */
  function onPickupPageSubmit(event) {
    event.preventDefault();
    if (page.submitting) return;
    page.error.hidden = true;
    page.submitting = true;
    page.submit.disabled = true;
    page.submit.textContent = "Момент…";
    lockUpsells(true);
    // The last read after an add failed: read the cart again, so the lines
    // and the tag rules are those of the cart going to checkout.
    if (page.cartStale) {
      trackCart(
        refreshSummary().then(function () {
          page.cartStale = false;
        }),
      );
    }
    cartSettled().then(function (failure) {
      if (failure === "add") {
        return submitFailed(
          "Продуктът не можа да се добави. Опитайте отново или продължете без него.",
        );
      }
      if (failure) return submitFailed("Количката не можа да се обнови. Опитайте отново.");
      // Validated only now: the cart read can have narrowed the delivery
      // choices, and a locker picked before a bike was added is gone.
      submitPickupPage();
    });
  }

  function submitFailed(message) {
    page.submitting = false;
    page.submit.disabled = false;
    page.submit.textContent = submitLabel() + " →";
    lockUpsells(false);
    pageError(message);
  }

  /**
   * The add-on offers, out of reach for as long as a submit runs: from the
   * press, through the wait for earlier adds and the cart write, until the
   * checkout opens or the submit gives up. Unlocked ones are those not added
   * yet; nothing is still adding by then, since the submit waited for it.
   */
  function lockUpsells(locked) {
    var u = page.upsell;
    if (!u) return;
    Array.prototype.forEach.call(u.list.querySelectorAll(".ss-upsell-card"), function (card) {
      var added = !!u.added[card.getAttribute("data-product")];
      var off = locked || added;
      var button = card.querySelector(".ss-upsell-add");
      var picker = card.querySelector(".ss-upsell-variant");
      if (button) button.disabled = off;
      if (picker) picker.disabled = off;
    });
  }

  function submitPickupPage() {
    var prefill = pickupPrefill();
    var toOffice = page.deliveryType === "to_office";
    if (!prefill.address.first_name) return submitFailed("Моля, въведете име.");
    if (!prefill.address.last_name) return submitFailed("Моля, въведете фамилия.");
    if (page.email && mode("email") === "required" && !prefill.email) {
      return submitFailed("Моля, въведете имейл.");
    }
    if (prefill.address.phone.replace(/\D/g, "").length < 6) {
      return submitFailed("Моля, въведете телефон.");
    }
    if (toOffice && (!page.office || page.officeCourier !== page.courier)) {
      return submitFailed("Моля, изберете офис или автомат.");
    }
    if (!toOffice && (!prefill.address.city || !prefill.address.address1)) {
      return submitFailed("Моля, въведете град и адрес.");
    }
    var invoice = invoiceDetails();
    if (invoice) {
      if (!invoice.company) return submitFailed("Моля, въведете фирма за фактурата.");
      // ЕИК is 9 or 13 digits. Not an ЕГН: the details ride to the checkout in
      // its URL, and a personal ID number has no business in a URL.
      if (!/^(\d{9}|\d{13})$/.test(invoice.eik)) {
        return submitFailed("Моля, въведете ЕИК (9 или 13 цифри).");
      }
      if (!invoice.mol) return submitFailed("Моля, въведете МОЛ.");
      if (!invoice.address) return submitFailed("Моля, въведете адреса на фирмата.");
    }
    if (page.terms && !page.terms.checked) {
      return submitFailed("Моля, приемете общите условия.");
    }

    var attributes = {};
    // Always written, empty when not asked for: a shopper who ticked the box,
    // came back and unticked it must not leave a company on the order.
    attributes[ATTR.invoiceCompany] = invoice ? invoice.company : "";
    attributes[ATTR.invoiceEik] = invoice ? invoice.eik : "";
    attributes[ATTR.invoiceVat] = invoice ? invoice.vat : "";
    attributes[ATTR.invoiceMol] = invoice ? invoice.mol : "";
    attributes[ATTR.invoiceAddress] = invoice ? invoice.address : "";
    attributes[ATTR.delivery] = page.deliveryType;
    attributes[ATTR.courier] = page.courier || "";
    // Cleared rather than omitted: a shopper who picked an office, went back
    // and switched to address delivery would otherwise leave the old office
    // sitting on the cart, and we would ship to it.
    attributes[ATTR.office] = toOffice ? page.office.id : "";
    attributes[ATTR.officeName] = toOffice
      ? [page.office.name, page.office.address].filter(Boolean).join(", ")
      : "";
    attributes[ATTR.officeKind] = toOffice ? page.office.kind || "office" : "";

    fetch(cartPath() + "/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("cart update failed with " + res.status);
        return res.json();
      })
      .then(function (cart) {
        window.location.href = prefilledCheckoutUrl(cart, prefill, attributes);
      })
      .catch(function () {
        // Deliberately not a silent fall-through to checkout. An order without
        // the office is worse than a shopper who has to press again: it reaches
        // the merchant looking complete and gets shipped to the wrong place.
        submitFailed("Изборът не можа да се запази. Опитайте отново.");
      });
  }

  // --- mount ---------------------------------------------------------------

  /**
   * Where the pickup page's own button goes, for a merchant who keeps the
   * theme's checkout button as it is. Themes vary wildly, so the anchor is
   * found by trying the conventional selectors in order; if none match we do
   * nothing rather than putting the button somewhere random.
   */
  var ANCHOR_SELECTORS = [
    'form[action*="/cart"] button[name="checkout"]',
    'form[action*="/cart"] input[name="checkout"]',
    'form[action="/cart"] [type="submit"]',
    ".cart__ctas",
    ".cart__checkout-button",
    "#checkout",
  ];

  /**
   * Every route a theme uses to hand the shopper to Shopify's own checkout.
   *
   * Dynamic payment buttons — "Buy it now", Shop Pay, the wallet buttons — are
   * deliberately absent. They skip the cart entirely, so there is no cart to
   * attach an office to, and intercepting them would either strand the shopper
   * or lose the item they picked. A merchant who wants those gone turns them
   * off in the theme, which is the honest place for that decision.
   */
  var CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], [formaction$="/checkout"],' +
    ' a[href="/checkout"], a[href$="/checkout"]';

  function isVisible(node) {
    if (!node || !node.isConnected || !node.getClientRects().length) return false;
    for (var parent = node; parent && parent.nodeType === 1; parent = parent.parentElement) {
      var style = window.getComputedStyle(parent);
      if (
        parent.hidden ||
        parent.hasAttribute("inert") ||
        parent.getAttribute("aria-hidden") === "true" ||
        style.display === "none" ||
        style.visibility === "hidden" ||
        style.visibility === "collapse" ||
        Number(style.opacity) === 0
      )
        return false;
      if (style.position === "fixed" || style.transform !== "none") {
        var rect = parent.getBoundingClientRect();
        if (rect.right <= 0 || rect.left >= document.documentElement.clientWidth) return false;
        if (style.position === "fixed" && (rect.bottom <= 0 || rect.top >= window.innerHeight))
          return false;
      }
    }
    return true;
  }

  function findAnchor() {
    for (var i = 0; i < ANCHOR_SELECTORS.length; i++) {
      var found = document.querySelectorAll(ANCHOR_SELECTORS[i]);
      for (var j = 0; j < found.length; j++) {
        if (found[j].parentNode && isVisible(found[j])) return found[j];
      }
    }
    return null;
  }

  /**
   * Put the pickup page's button on the page, or put it back.
   *
   * Called on load and again whenever the page changes, because a cart drawer,
   * an AJAX cart update or a section re-render all replace the checkout button
   * and can take ours down with it. With replacing on, the theme's own button
   * opens the pickup page itself (interceptCheckout) and nothing is added.
   */
  function mount() {
    if (!config) return;
    if (!cfg.cart || !(cfg.cart.items || []).length) return;
    if (cfg.replaceCheckout) return;
    if (document.querySelector(".ss-open-page")) return;
    var anchor = findAnchor();
    if (!anchor) return;
    var button = el("button", { type: "button", class: "ss-open-page", text: submitLabel() });
    applyPageAppearance(button);
    button.addEventListener("click", openPickupPage);
    anchor.parentNode.insertBefore(button, anchor);
  }

  /** The store's cart page, honouring a locale prefix like /en-gb/cart. */
  function cartPath() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return String(root).replace(/\/+$/, "") + "/cart";
  }

  /**
   * The checkout control inside an event's path, looking through shadow roots.
   *
   * `composedPath` is what makes this work where a plain `event.target` does
   * not: cart and upsell apps increasingly render their checkout button inside
   * a shadow root, and from the outside the event target is only the host
   * element. Walking the composed path finds the real button without us having
   * to know which app drew it.
   */
  function checkoutControlIn(event) {
    var path = typeof event.composedPath === "function" ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches(CHECKOUT_SELECTOR)) {
        return node;
      }
    }
    return null;
  }

  /**
   * Stop a click or submit that would open Shopify's checkout, and open the
   * pickup page first. The page ends in Shopify's checkout all the same, with
   * the office written onto the cart.
   *
   * Runs in the capture phase so it lands before the theme's own handler; a
   * theme that calls `stopPropagation` on its checkout button would otherwise
   * never let the event reach us.
   *
   * The guard clauses are the important part. We interfere only when the
   * merchant asked us to replace checkout and our config actually loaded. If
   * either is untrue the event runs exactly as the theme intended — the same
   * fail-open rule the rest of this file lives under, applied to the one place
   * where getting it wrong would stop a store taking orders at all.
   *
   * Works from any page, drawer included: the pickup page opens over whatever
   * the shopper is looking at.
   */
  function interceptCheckout(event) {
    if (!cfg.replaceCheckout || !config) return;
    if (!checkoutControlIn(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openPickupPage();
  }

  /**
   * Re-scan when the page changes.
   *
   * Debounced through the microtask-ish end of the current frame: a section
   * re-render fires hundreds of mutations, and mounting is a DOM write, so
   * doing it per mutation would fight the theme for the layout.
   */
  function watch() {
    if (typeof MutationObserver !== "function") return;
    var pending = false;
    function scheduleMount() {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        mount();
      }, 100);
    }
    new MutationObserver(scheduleMount).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "open", "inert"],
    });
    document.addEventListener("transitionend", scheduleMount, true);
    document.addEventListener("animationend", scheduleMount, true);
    window.addEventListener("resize", scheduleMount);
  }

  /** Did this request add something to Shopify's cart? */
  function isCartAdd(url, method) {
    return (
      String(method || "GET").toUpperCase() === "POST" &&
      /\/cart\/add(\.js)?(\?|$)/.test(String(url))
    );
  }

  /**
   * Open the pickup page once a product lands in the cart, for merchants who
   * asked for it. Themes add to the cart through fetch or XHR and then open
   * their own drawer; we watch for a successful add rather than a click, so a
   * failed add (sold out) never opens a page for an empty order. A theme that
   * posts a plain form navigates to the cart instead, where the checkout
   * button still leads here.
   */
  function openAfterAddToCart() {
    // A product added while the page is already open came from its own offers.
    function pageOpen() {
      return !!(page && !page.root.hidden);
    }
    var realFetch = window.fetch;
    if (typeof realFetch === "function") {
      window.fetch = function (input, init) {
        var url = typeof input === "string" ? input : (input && input.url) || "";
        var method = (init && init.method) || (input && input.method);
        var result = realFetch.apply(this, arguments);
        if (isCartAdd(url, method)) {
          result.then(
            function (res) {
              if (res && res.ok && !pageOpen()) setTimeout(openPickupPage, 300);
            },
            function () {},
          );
        }
        return result;
      };
    }
    var open = window.XMLHttpRequest.prototype.open;
    var send = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__ssCartAdd = isCartAdd(url, method);
      return open.apply(this, arguments);
    };
    window.XMLHttpRequest.prototype.send = function () {
      if (this.__ssCartAdd) {
        this.addEventListener("load", function () {
          if (this.status >= 200 && this.status < 300 && !pageOpen()) {
            setTimeout(openPickupPage, 300);
          }
        });
      }
      return send.apply(this, arguments);
    };
  }

  function start() {
    // Bound before the config call resolves, so a shopper who clicks checkout
    // while our request is still in flight is not handed to Shopify's checkout
    // by a race. Until `config` is set the handler returns immediately, which
    // is the correct behaviour anyway.
    window.addEventListener("click", interceptCheckout, true);
    window.addEventListener("submit", interceptCheckout, true);

    request("/api/checkout/config")
      .then(function (data) {
        config = data.config;
        previewOnly = data.preview === true;
        mount();
        watch();
        if (config.autoOpenOnAdd) openAfterAddToCart();
      })
      .catch(function () {
        // Checkout disabled, token rotated, or API down — leave the theme's
        // own checkout exactly as it was. `config` stays null, so the
        // interception handlers above do nothing.
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
